import * as React from "react"
import { useRef, useState } from "react"

import { ConfirmModal } from "~/src/components/AdaKit/Modal"

import "./ConfirmationDialog.scss"

import { ButtonKind } from "~/src/components/AdaKit/Button"

type ModalContent = {
  title: string
  description?: string
}

type ConfirmationOptions = {
  confirmButtonText?: string
  confirmButtonKind?: ButtonKind
}

export function useConfirmation({ confirmButtonText = "Ok", confirmButtonKind = "danger" }: ConfirmationOptions = {}): [
  (title: string, description?: string) => Promise<boolean>,
  () => React.ReactElement,
] {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [modalContent, setModalContent] = useState<ModalContent>({ title: "" })
  const resolveCallbackRef = useRef<((confirmed: boolean) => void) | null>(null)

  const open = (content: ModalContent) => {
    setModalContent(content)
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
  }

  const onConfirm = () => {
    if (resolveCallbackRef.current) {
      resolveCallbackRef.current(true)
    }
    close()
  }

  const onCancel = () => {
    if (resolveCallbackRef.current) {
      resolveCallbackRef.current(false)
    }
    close()
  }

  const confirm = (title: string, description?: string): Promise<boolean> => {
    open({ title, description })

    return new Promise((resolve) => {
      resolveCallbackRef.current = resolve
    })
  }

  const ConfirmationDialog = () => {
    return (
      <ConfirmModal
        title={modalContent.title}
        okBtn={{ kind: confirmButtonKind, text: confirmButtonText }}
        open={isOpen}
        onAccept={onConfirm}
        onClose={onCancel}
      >
        <p>{modalContent.description}</p>
      </ConfirmModal>
    )
  }

  return [confirm, ConfirmationDialog]
}
